import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import { QRCodeCanvas } from 'qrcode.react';
import { useMutation } from '@tanstack/react-query';
import { getProvisionedNumber } from '@/api/reservedNumbers';

import IphoneQRCodeCameraSVG from './IphoneQRCodeCameraSVG';
import AndroidQRCodeCameraSVG from './AndroidQRCodeCameraSVG';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';

import IphoneFullImage from '@/assets/images/onboarding/iphone/iPhone_full.png';
import IphonePhoneAppImage from '@/assets/images/onboarding/iphone/iPhonePhoneAppIcon.png';
import IphoneKeypadImage from '@/assets/images/onboarding/iphone/iPhoneKeypadIcon.png';
import IphoneCallForwardImage from '@/assets/images/onboarding/iphone/iPhone_call_forward_full_screen.png';
import AndroidFullImage from '@/assets/images/onboarding/iphone/android_full.png';
import AndroidMMINotif from '@/assets/images/onboarding/iphone/AndroidMMICodeNotification.png';

const formatPhoneNumber = (phoneNumber: string): string => {
	return phoneNumber.replace(/[^0-9]/g, '');
};

const getSpecialCharsInstruction = (value: string): string => {
	const specialChars = value.match(/[^\w\s]/g);
	if (specialChars?.length) {
		const uniqueChars = [...new Set(specialChars)];
		return `, including ${uniqueChars.join(', ')} character(s)`;
	}
	return '';
};

// Android Dialer Information
interface AndroidDialerPreviewProps {
	qrValue: string;
	children?: React.ReactNode;
}

const AndroidDialerPreview: React.FC<AndroidDialerPreviewProps> = ({ qrValue, children }) => {
	return (
		<>
			{children}
			<div className="h-96 pt-sm  mt-4">
				<div className="flex h-full w-full justify-center bg-primary-content bg-gray-200 rounded-b-lg">
					<svg height="auto" viewBox="0 0 865 1924">
						<image href={AndroidFullImage} />
						<text
							className="text-6xl"
							x="440"
							y="700"
							textAnchor="middle"
							fontSize="32"
							fill="black"
							fontFamily="sans-serif"
						>
							{qrValue}
						</text>
					</svg>
				</div>
			</div>
		</>
	);
};

// Android MMI Notif Props
interface AndroidNotifyMMIProps {
	children?: React.ReactNode;
}
const AndroidMMINotification: React.FC<AndroidNotifyMMIProps> = ({ children }) => {
	return (
		<>
			{children}
			<div className="p-sm my-2">
				<div className="flex h-20 justify-center items-center bg-gray-200 rounded-b-lg p-sm">
					<img
						src={AndroidMMINotif}
						alt="Android MMI Notification"
						className="h-3/5 object-contain"
					/>
				</div>
			</div>
		</>
	);
};

// Component for the Instant Setup Modal
const InstantSetupModal = ({
	isOpen,
	onClose,
	qrValue,
	provisionedNumber,
}: {
	isOpen: boolean;
	onClose: () => void;
	qrValue: string;
	provisionedNumber: string | null;
}) => (
	<Modal
		title={<span className="p-3 text-xl font-semibold text-gray-800">Instant Setup</span>}
		open={isOpen}
		onCancel={onClose}
		footer={null}
	>
		<div className="flex flex-col items-center my-6 px-4">
			{provisionedNumber ? (
				<>
					<div className="p-4 bg-white rounded-xl shadow-lg border border-gray-100">
						<QRCodeCanvas value={`tel:${qrValue}`} size={200} />
					</div>
					<p className="text-gray-700 text-center mt-6 text-base leading-relaxed max-w-md">
						Scan this QR code with your phone's camera to quickly set up your AI receptionist.
					</p>
					<div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-6 max-w-md">
						<p className="text-red-700 text-center text-base">
							<strong className="text-red-800">⚠️ Caution:</strong> Scanning this QR code will
							forward all calls from the registered number on the device you scan it with, to your
							AI Receptionist.
						</p>
					</div>
				</>
			) : (
				<div className="flex items-center space-x-3">
					<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
					<p className="text-gray-600 text-base">Generating QR code...</p>
				</div>
			)}
		</div>
	</Modal>
);

// Component for the Manual Setup Modal
const ManualSetupModal = ({
	isOpen,
	onClose,
	isAndroid,
	qrValue,
	withExtraInfo,
	ExtraInstructions,
}: {
	isOpen: boolean;
	onClose: () => void;
	qrValue: string;
	isAndroid?: boolean;
	withExtraInfo?: boolean;
	ExtraInstructions: React.ReactNode;
}) => {
	const IphoneModal = (
		<Modal
			title={<span className="text-xl">Manual Setup</span>}
			open={isOpen}
			onCancel={onClose}
			footer={null}
		>
			<div className="space-y-4 px-4 max-h-[70vh] overflow-y-auto">
				<p className="text-base leading-relaxed">
					<span className="font-semibold">Step 1.</span> Open the "Phone" app on your device and go
					to the "Keypad".
				</p>
				<div className="flex justify-center gap-4">
					<img alt="iPhone Phone App Icon" className="h-16" src={IphonePhoneAppImage} />
					<img alt="iPhone Keypad Icon" className="h-16" src={IphoneKeypadImage} />
				</div>

				<p className="text-base">
					<span className="font-semibold">Step 2.</span> Enter the following phone number
					{getSpecialCharsInstruction(qrValue)}, and press the green call button.
				</p>

				<div className="flex justify-center py-4">
					<svg className="h-60" viewBox="0 0 1393 2300">
						<image height="2300" href={IphoneFullImage} width="1393" />
						<rect x="490" y="380" width="400" height="160" fill="white" />
						<text className="text-8xl" x="690" y="500" textAnchor="middle">
							{qrValue}
						</text>
					</svg>
				</div>

				{withExtraInfo ? (
					<div>
						<p className="text-base leading-relaxed">
							<span className="font-semibold">Step 3.</span> Once set up, you'll see a screen
							detailing the changed settings, which you can then{' '}
							<span className="font-bold">dismiss</span>.
						</p>
						{ExtraInstructions}
					</div>
				) : (
					<p className="text-base leading-relaxed">
						If everything is set up, you'll{' '}
						<span className="font-semibold">hear a short audio confirmation</span>.
					</p>
				)}
			</div>
		</Modal>
	);

	const AndroidModal = (
		<Modal
			title={<span className="text-xl">Manual Setup</span>}
			open={isOpen}
			onCancel={onClose}
			footer={null}
		>
			<div className="space-y-4 px-4 max-h-[70vh] overflow-y-auto">
				<p className="text-base leading-relaxed">
					<span className="font-semibold">Step 1.</span> Open your device's default{' '}
					<span className="font-semibold">Phone app</span> and go to the{' '}
					<span className="font-semibold">Dialer screen</span>.
				</p>
				<div className="flex justify-center gap-4">
					<img alt="iPhone Phone App Icon" className="h-16" src={IphonePhoneAppImage} />
				</div>

				<AndroidDialerPreview qrValue={qrValue}>
					<p className="text-base">
						<span className="font-semibold">Step 2.</span> Enter the following phone number
						{getSpecialCharsInstruction(qrValue)}, and press the green call button.
					</p>
				</AndroidDialerPreview>

				{withExtraInfo ? (
					ExtraInstructions
				) : (
					<p className="text-base leading-relaxed">
						If everything is set up, you'll{' '}
						<span className="font-semibold">hear a short audio confirmation</span>.
					</p>
				)}
			</div>
		</Modal>
	);

	return <>{!isAndroid ? IphoneModal : AndroidModal}</>;
};

// Component for the iPhone Extra image/content
const IphoneExtraContent = () => (
	<div className="text-center my-4">
		<div className="flex w-full justify-center bg-gray-200 p-sm rounded-b-lg">
			<img
				className="h-80"
				src={IphoneCallForwardImage}
				alt="iPhone showing call forwarding settings"
			/>
		</div>
	</div>
);

const QRCodeGenerator = ({
	prefix,
	postfix,
	isAndroid,
	withExtraInfo,
}: {
	prefix: string;
	postfix?: string;
	isAndroid?: boolean;
	withExtraInfo?: boolean;
}) => {
	const [provisionedNumber, setProvisionedNumber] = useState<string | null>(null);
	const [isInstantSetupModalOpen, setInstantSetupModalOpen] = useState(false);
	const [isManualSetupModalOpen, setManualSetupModalOpen] = useState(false);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const selectedAgent = useAppSelector(getSelectedAgent);

	const getProvisionNumberMutation = useMutation({
		mutationKey: ['provisionedNumberApi'],
		mutationFn: getProvisionedNumber,
		onSuccess(data) {
			const formattedNumber = formatPhoneNumber(data.provisioned_number || '');
			setProvisionedNumber(formattedNumber);
		},
	});

	useEffect(() => {
		getProvisionNumberMutation.mutate(selectedAgentId as string);
	}, [selectedAgent?.number]);

	const qrValue = provisionedNumber ? `${prefix}${provisionedNumber}${postfix || ''}` : '';

	const iphoneExtraContent = <IphoneExtraContent />;

	const androidExtraContent = (
		<AndroidMMINotification>
			<span>
				Wait a few Seconds. If everything is set up correctly, a small popup will appear indicating
				that the
				<span className="font-bold">"MMI Code Started".</span>.
			</span>
		</AndroidMMINotification>
	);

	return (
		<div className="max-w-4xl mx-auto max-h-[500px] overflow-y-auto p-6 bg-gray-50 rounded-lg shadow-md">
			<p className="text-lg">
				When ready, <b>click Start Instant Setup</b> and open the default camera app on your{' '}
				{isAndroid ? 'Android Phone' : 'iPhone'}. <b>Scan the QR code.</b>
			</p>

			<div className="mb-0.5">
				<div className="flex h-44 w-full justify-center px-sm py-xl bg-gray-200 rounded-t-lg">
					{isAndroid ? (
						<AndroidQRCodeCameraSVG>{qrValue}</AndroidQRCodeCameraSVG>
					) : (
						<IphoneQRCodeCameraSVG>{qrValue}</IphoneQRCodeCameraSVG>
					)}
				</div>
				<p className="break-words font-sans leading-normal text-md my-4">
					{!isAndroid ? (
						<>
							<span className="font-bold">Press the yellow call button.</span>
							When successfully set up
							{withExtraInfo ? (
								<>
									{' '}
									you'll be shown a screen with details of settings changed which you can then{' '}
									<span className="font-bold">dismiss</span>.{iphoneExtraContent}
								</>
							) : (
								<>
									{' '}
									you will <span className="font-bold">hear a short Confirmation</span>.
								</>
							)}
						</>
					) : (
						//----------- sibling compenent starts here
						<>
							{/* Android Base Component */}
							<AndroidDialerPreview qrValue={qrValue}>
								<span>
									Press <span className="font-bold">Call or Open</span> to open your Android Phone
									App, then <span className="font-bold">Press the call button</span>
								</span>
							</AndroidDialerPreview>
							<br />
							{withExtraInfo ? (
								<AndroidMMINotification>
									<span>
										If everything is set up correctly, a small popup will appear indicating that the
										<span className="font-bold">"MMI Code Started".</span>.
									</span>
								</AndroidMMINotification>
							) : (
								<span>
									When successfully set up,
									<span className="font-bold">you will hear a short Audio Confirmation</span>.
								</span>
							)}
						</>
						// sibling ends starts here ---------------
					)}
				</p>
			</div>
			<button
				onClick={() => setInstantSetupModalOpen(true)}
				className="w-full bg-blue-600 text-base text-white font-bold py-4 rounded-lg mb-2 hover:bg-blue-700"
			>
				Start Instant Setup
			</button>
			<button
				onClick={() => setManualSetupModalOpen(true)}
				className="w-full bg-gray-300 text-black font-bold py-4 rounded-lg hover:bg-gray-400"
			>
				Manual Setup
			</button>

			<InstantSetupModal
				isOpen={isInstantSetupModalOpen}
				onClose={() => setInstantSetupModalOpen(false)}
				qrValue={qrValue}
				provisionedNumber={provisionedNumber}
			/>
			<ManualSetupModal
				isOpen={isManualSetupModalOpen}
				onClose={() => setManualSetupModalOpen(false)}
				qrValue={qrValue}
				withExtraInfo={withExtraInfo}
				ExtraInstructions={isAndroid ? androidExtraContent : iphoneExtraContent}
				isAndroid={isAndroid}
			/>
		</div>
	);
};

export default QRCodeGenerator;
